import Checkbox, { StatelessCheckbox } from '../Checkbox';
import { FormCheck, FormControl } from 'react-bootstrap';

import { DropdownMenu } from '../../screens/subscriptionComponents/DropdownMenu';
import { ElipsisTooltip } from '../ElipsisTooltip';
import Image from '../Image';
import Loader from '../Loader';
import cx from 'classnames';
import styles from './ItemSelectListUI.module.scss';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { usePrevious } from 'react-use';

export const ItemSelectListUI = ({
  items,
  selected,
  className,
  onChange,
  loading,
  hasData,
  hasMore,
  fetchMoreData,
  noDataMessage = 'No results',
  onButtonClick,
  buttonText,
  minHeight,
  formatData,
  dataType,
  itemClassName,
  variant = 'default',
  placeholder, // show image placeholder
  actionMenu = null,
  isV2 = false,
  missingImageName = [],
}) => {
  const prevItems = usePrevious(items);
  const selectable = Boolean(onChange);

  const [sentryRef, { rootRef }] = useInfiniteScroll({
    loading,
    hasNextPage: hasMore,
    onLoadMore: fetchMoreData,
    rootMargin: '0px 0px 120px 0px',
  });

  const handleClick = (item) => () => {
    // TODO: support for multiple selection
    if (Array.isArray(selected)) {
      let updatedList;

      if (dataType === 'object') {
        updatedList = selected.find((arrItem) => arrItem.id === item.id)
          ? selected.filter((arrItem) => arrItem.id !== item.id)
          : [...selected, item];
      } else {
        updatedList = selected.includes(item.id)
          ? selected.filter((id) => id !== item.id)
          : [...selected, item.id];
      }

      onChange?.(updatedList, item);
    } else {
      onChange?.(dataType === 'object' ? item : item.id);
    }
  };

  const itemList = loading && items.length === 0 ? prevItems || [] : items;
  const placeholdersVisible = !hasData || !itemList.length;
  const hasButton = typeof buttonText !== 'undefined';

  return (
    <div
      ref={rootRef}
      className={cx(
        styles.root,
        className,
        hasButton && styles.hasButton,
        variant === 'checkbox' && styles.variantCheckbox,
        !selectable && styles.notSelectable,
        isV2 && styles.design2
      )}
      style={{
        minHeight,
      }}>
      {hasButton && (
        <div className={cx(styles.itemButton)} onClick={onButtonClick}>
          {buttonText}
        </div>
      )}

      <Loader loading={loading} position="sticky" className={styles.loader} />

      {placeholdersVisible && (
        <>
          {!loading && <div className={styles.noDataMessage}>{noDataMessage}</div>}
          <div className={cx(styles.noItem)}></div>
          <div className={cx(styles.noItem)}></div>
          <div className={cx(styles.noItem)}></div>
          {!hasButton && <div className={cx(styles.noItem)}></div>}
        </>
      )}

      <div className={styles.listContainer}>
        {itemList.map((item, index) => {
          const formattedItem = formatData ? formatData(item) : item;
          const { image, title, description, id, isSectionTitle , name , organization_owner_email, organization_owner_name } = formattedItem;
          const imageName = missingImageName?.[index] || null;
          const _title = `${title || name || organization_owner_name} ${imageName ? `: ${imageName}` : ''}`;

          if (isSectionTitle) {
            return (
              <ElipsisTooltip title={_title}>
                {({ ref, onMouseEnter, onMouseLeave }) => (
                  <div
                    ref={ref}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    key={id}
                    className={cx(styles.titleItem, isV2 && styles.alignLeft)}>
                    {_title}
                  </div>
                )}
              </ElipsisTooltip>
            );
          }

          let isSelected = false;

          if (dataType === 'object') {
            isSelected = Array.isArray(selected)
              ? Boolean(selected.find((i) => i.id === id))
              : id === selected.id;
          } else {
            isSelected = Array.isArray(selected) ? selected.includes(id) : id === selected;
          }

          return (
            <div
              key={formattedItem.id}
              className={cx(
                styles.item,
                styles.normalItem,
                itemClassName,
                isSelected && styles.selected
              )}
              onClick={handleClick(item)}>
              {(image || placeholder) && (
                <div className={styles.imageContainer}>
                  <Image
                    src={image || placeholder}
                    ratio={1}
                    placeholderWhileLoading
                    hideWithNoSrc
                    rounded
                  />
                </div>
              )}

              <div className={styles.contentContainer}>
                <ElipsisTooltip title={_title}>
                  {({ ref, onMouseEnter, onMouseLeave }) => (
                    <div
                      className={styles.title}
                      ref={ref}
                      onMouseEnter={onMouseEnter}
                      onMouseLeave={onMouseLeave}>
                      {_title}
                    </div>
                  )}
                </ElipsisTooltip>
                <div className={styles.description}>{description || organization_owner_email}</div>
              </div>

              {actionMenu && <DropdownMenu menu={actionMenu} item={item} />}

              {variant === 'checkbox' && <StatelessCheckbox type="checkbox" checked={isSelected} />}
            </div>
          );
        })}

        {hasMore && <div ref={sentryRef}></div>}
      </div>
    </div>
  );
};
